import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';

import { MAX_DESCRIPTION_LEN, parseFrontmatter, setFrontmatterField, type SkillDetail } from '@/api-models/skills';
import { ReadOnlyTag } from '@/features/settings/components/ReadOnlyTag';
import { SkillFileTree } from '@/features/settings/components/SkillFileTree';
import { Button } from '@/ui/button/Button';
import { CodeEditor } from '@/ui/CodeEditor';
import { Input } from '@/ui/Input';
import { SegmentedControl } from '@/ui/SegmentedControl';
import { Textarea } from '@/ui/Textarea';

const SKILL_MD = 'SKILL.md';

type Mode = 'form' | 'raw';

/** What the editor needs from whichever surface hosts it — team or admin. */
export interface SkillEditorApi {
  loadFile: (path: string) => Promise<string>;
  saveSkillMd: (source: string) => Promise<void>;
  saveFile: (path: string, content: string) => Promise<void>;
  createFile: (path: string) => Promise<void>;
  deleteFile: (path: string) => Promise<void>;
  /** Refetch the skill after a structural change (a file added or removed). */
  refresh: () => Promise<void>;
}

/**
 * Editor for a whole skill bundle: `SKILL.md` plus whatever it ships alongside.
 *
 * `SKILL.md` gets two modes. **Form** gives `name` and `description` labelled fields above the
 * markdown body, because the description is the only thing the model sees when deciding whether to
 * use a skill — burying it in YAML a user has to know how to write is how skills end up never
 * being reached for. **Raw** is the whole document, and the only way to reach keys the form does
 * not model (`allowed-tools`, `metadata`, `license`).
 *
 * Form edits rewrite exactly one frontmatter line and never reserialize, so unmodelled keys
 * survive byte-identical. When the document uses YAML the line-based parser can't read
 * confidently — a block scalar, no fence — the form is withdrawn and raw is forced, rather than
 * risking a silent mangle.
 *
 * Bundled files are edited in CodeMirror, which is what makes `scripts/` and `references/`
 * tolerable to work in.
 */
export function SkillEditor({
  skill,
  readOnly = false,
  api,
}: {
  skill: SkillDetail;
  readOnly?: boolean;
  api: SkillEditorApi;
}) {
  const [selected, setSelected] = useState(SKILL_MD);
  const [mode, setMode] = useState<Mode>('form');
  const [saving, setSaving] = useState(false);

  // The `SKILL.md` draft comes with the skill; bundled files are fetched on first open.
  const [skillMd, setSkillMd] = useState(skill.skill_md);
  const [fileDrafts, setFileDrafts] = useState<Record<string, string>>({});
  const [savedFiles, setSavedFiles] = useState<Record<string, string>>({});
  const [loadingFile, setLoadingFile] = useState(false);

  // Re-seed when a different skill loads into the same component.
  useEffect(() => {
    setSkillMd(skill.skill_md);
    setFileDrafts({});
    setSavedFiles({});
    setSelected(SKILL_MD);
  }, [skill.id, skill.skill_md]);

  const loadFile = api.loadFile;
  useEffect(() => {
    if (selected === SKILL_MD || selected in fileDrafts) return;
    let cancelled = false;
    setLoadingFile(true);
    loadFile(selected)
      .then((content) => {
        if (cancelled) return;
        setFileDrafts((d) => ({ ...d, [selected]: content }));
        setSavedFiles((d) => ({ ...d, [selected]: content }));
      })
      .catch(() => {
        if (!cancelled) toast.error(`Could not open ${selected}`);
      })
      .finally(() => {
        if (!cancelled) setLoadingFile(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selected, fileDrafts, loadFile]);

  const parsed = useMemo(() => parseFrontmatter(skillMd), [skillMd]);
  const formUsable = Boolean(parsed?.frontmatter.editable);
  useEffect(() => {
    if (!formUsable) setMode('raw');
  }, [formUsable]);

  const selectedMeta = skill.files.find((f) => f.path === selected);
  const isBinary = selectedMeta?.is_binary ?? false;

  const dirtyPaths = useMemo(() => {
    const dirty = Object.keys(fileDrafts).filter((p) => fileDrafts[p] !== savedFiles[p]);
    return skillMd !== skill.skill_md ? [SKILL_MD, ...dirty] : dirty;
  }, [fileDrafts, savedFiles, skillMd, skill.skill_md]);

  const description = parsed?.frontmatter.description ?? '';
  const tooLong = description.length > MAX_DESCRIPTION_LEN;

  // Warn before losing unsaved work to a reload or a closed tab. In-app navigation is a plain
  // link, so this is the case the router can't cover.
  useEffect(() => {
    if (dirtyPaths.length === 0) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirtyPaths.length]);

  const handleSave = useCallback(async () => {
    if (dirtyPaths.length === 0 || saving) return;
    setSaving(true);
    try {
      // Only what changed — a skill with twenty files should not rewrite twenty rows to fix a typo.
      for (const path of dirtyPaths) {
        if (path === SKILL_MD) {
          await api.saveSkillMd(skillMd);
        } else {
          await api.saveFile(path, fileDrafts[path] ?? '');
        }
      }
      setSavedFiles((saved) => ({ ...saved, ...fileDrafts }));
      await api.refresh();
      toast.success(dirtyPaths.length === 1 ? 'Saved' : `Saved ${dirtyPaths.length} files`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not save');
    } finally {
      setSaving(false);
    }
  }, [api, dirtyPaths, fileDrafts, saving, skillMd]);

  // ⌘S / Ctrl-S, the reflex anyone editing a file has.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        void handleSave();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handleSave]);

  function setField(key: 'name' | 'description', value: string) {
    setSkillMd((current) => setFrontmatterField(current, key, value));
  }

  /** Replace everything after the closing frontmatter fence, leaving the fence block untouched. */
  function setBody(body: string) {
    setSkillMd((current) => {
      const lines = current.split('\n');
      if (lines[0]?.trim() !== '---') return current;
      const closing = lines.findIndex((line, i) => i > 0 && line.trim() === '---');
      if (closing === -1) return current;
      return [...lines.slice(0, closing + 1), body].join('\n');
    });
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          {selected === SKILL_MD && (
            <SegmentedControl<Mode>
              value={mode}
              onChange={setMode}
              ariaLabel="Editor mode"
              options={
                // Withdrawn rather than disabled when the document is beyond the field editor: the
                // shared control has no disabled state, and an option that does nothing is worse
                // than one that isn't offered.
                formUsable
                  ? [
                      { value: 'form', label: 'Fields' },
                      { value: 'raw', label: 'Raw' },
                    ]
                  : [{ value: 'raw', label: 'Raw' }]
              }
            />
          )}
          {readOnly && <ReadOnlyTag />}
          {selected === SKILL_MD && !formUsable && (
            <span className="text-xs text-fg-tertiary">
              This SKILL.md uses YAML the field editor can&rsquo;t change safely, so it opens as raw text.
            </span>
          )}
        </div>
        {!readOnly && (
          <div className="flex items-center gap-3">
            {dirtyPaths.length > 0 && (
              <span className="text-xs text-fg-tertiary">
                {dirtyPaths.length} unsaved {dirtyPaths.length === 1 ? 'file' : 'files'}
              </span>
            )}
            <Button
              type="button"
              variant="primary"
              size={9}
              disabled={dirtyPaths.length === 0 || saving || tooLong}
              onClick={handleSave}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        )}
      </div>

      <div className="flex min-h-128 gap-4">
        <SkillFileTree
          files={skill.files}
          selected={selected}
          readOnly={readOnly}
          onSelect={setSelected}
          onCreate={async (path) => {
            await api.createFile(path);
            setFileDrafts((d) => ({ ...d, [path]: '' }));
            setSavedFiles((d) => ({ ...d, [path]: '' }));
            await api.refresh();
          }}
          onDelete={async (path) => {
            try {
              await api.deleteFile(path);
              setFileDrafts(({ [path]: _dropped, ...rest }) => rest);
              setSavedFiles(({ [path]: _dropped, ...rest }) => rest);
              if (selected === path) setSelected(SKILL_MD);
              await api.refresh();
              toast.success(`Deleted ${path}`);
            } catch (e) {
              toast.error(e instanceof Error ? e.message : `Could not delete ${path}`);
            }
          }}
        />

        <div className="flex min-w-0 flex-1 flex-col gap-4">
          {selected === SKILL_MD ? (
            mode === 'form' && parsed ? (
              <SkillMdForm
                name={parsed.frontmatter.name ?? ''}
                description={description}
                body={parsed.body}
                readOnly={readOnly}
                tooLong={tooLong}
                onName={(v) => setField('name', v)}
                onDescription={(v) => setField('description', v)}
                onBody={setBody}
              />
            ) : (
              <div className="flex min-h-0 flex-1 flex-col gap-1.5">
                <CodeEditor
                  path="SKILL.md"
                  value={skillMd}
                  readOnly={readOnly}
                  onChange={setSkillMd}
                  className="min-h-112 flex-1"
                />
                <p className="text-xs text-fg-tertiary">
                  YAML frontmatter between <code>---</code> fences, then markdown. <code>allowed-tools</code>{' '}
                  pre-approves those tools for the whole session once this skill is loaded — only list what the skill
                  genuinely needs.
                </p>
              </div>
            )
          ) : isBinary ? (
            <div className="flex flex-1 items-center justify-center rounded-lg border border-line bg-surface-1 text-xs text-fg-tertiary">
              {selected} is a binary file. Replace it by re-importing the skill&rsquo;s zip.
            </div>
          ) : loadingFile && !(selected in fileDrafts) ? (
            <div className="flex flex-1 items-center justify-center rounded-lg border border-line bg-surface-1 text-xs text-fg-tertiary">
              Loading {selected}…
            </div>
          ) : (
            <div className="flex min-h-0 flex-1 flex-col gap-1.5">
              <CodeEditor
                path={selected}
                value={fileDrafts[selected] ?? ''}
                readOnly={readOnly}
                onChange={(v) => setFileDrafts((d) => ({ ...d, [selected]: v }))}
                className="min-h-112 flex-1"
              />
              <p className="text-xs text-fg-tertiary">
                {selectedMeta?.is_executable
                  ? 'Executable — the agent can run this with bash.'
                  : 'The agent reads this when SKILL.md points it here.'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SkillMdForm({
  name,
  description,
  body,
  readOnly,
  tooLong,
  onName,
  onDescription,
  onBody,
}: {
  name: string;
  description: string;
  body: string;
  readOnly: boolean;
  tooLong: boolean;
  onName: (v: string) => void;
  onDescription: (v: string) => void;
  onBody: (v: string) => void;
}) {
  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-1.5">
        <label htmlFor="sk-name" className="text-xs font-medium text-fg-secondary">
          Name
        </label>
        <Input id="sk-name" value={name} disabled={readOnly} onChange={(e) => onName(e.target.value)} />
        <p className="text-xs text-fg-tertiary">
          Lowercase letters, digits, and single hyphens. Renaming changes how the agent refers to this skill.
        </p>
      </div>

      <div className="flex flex-col gap-1.5">
        <div className="flex items-baseline justify-between gap-3">
          <label htmlFor="sk-description" className="text-xs font-medium text-fg-secondary">
            When should the agent use this?
          </label>
          <span className={`text-2xs tabular-nums ${tooLong ? 'text-danger' : 'text-fg-tertiary'}`}>
            {description.length}/{MAX_DESCRIPTION_LEN}
          </span>
        </div>
        <Textarea
          id="sk-description"
          value={description}
          disabled={readOnly}
          className="h-20 text-sm"
          onChange={(e) => onDescription(e.target.value)}
        />
        <p className="text-xs text-fg-tertiary">
          Shown to the agent in every request. Name the trigger, not just the topic — this line alone decides whether
          the skill is ever reached for.
        </p>
      </div>

      <div className="flex flex-col gap-1.5">
        <label className="text-xs font-medium text-fg-secondary">
          Instructions <span className="text-fg-tertiary">(Markdown)</span>
        </label>
        <CodeEditor path="body.md" value={body} readOnly={readOnly} onChange={onBody} className="h-96" />
        <p className="text-xs text-fg-tertiary">
          Loaded in full only when the agent calls this skill, so length here costs nothing until it&rsquo;s used. Point
          at bundled files by path, e.g. <code>scripts/check.sh</code>.
        </p>
      </div>
    </div>
  );
}
